import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { useStepUpAuth } from '../hooks/useStepUpAuth';
import StepUpAuthModal from './StepUpAuthModal';
import {
  initiateUpworkOAuth,
  getUpworkOAuthStatus,
  refreshUpworkToken,
  disconnectUpwork,
  updateUpworkOAuthCredentials,
  getProfileImportStatus,
} from '../services/tenantService';
import { NotificationChannels } from './NotificationChannels';
import {
  Save,
  Briefcase,
  Bell,
  CheckCircle,
  XCircle,
  Loader,
  Link2,
  RefreshCw,
  Unlink,
  AlertCircle,
  Clock
} from 'lucide-react';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';
import UpworkMcpConnectionCard from './UpworkMcpConnectionCard';

export function TenantIntegrations() {
  const { tenant, tenantConfig, refreshConfig } = useTenant();
  const { canUpdateTenantConfig } = usePermissions();
  const canEdit = canUpdateTenantConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});
  const [activationWarning, setActivationWarning] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [profileImportStatus, setProfileImportStatus] = useState(null);
  const { requireStepUp, stepUpModalProps } = useStepUpAuth();

  // OAuth client credentials form state
  const [oauthCredentials, setOauthCredentials] = useState({
    clientId: '',
    clientSecret: '',
  });
  const [hasClientSecretChanged, setHasClientSecretChanged] = useState(false);

  const getErrorMessage = (error, fallback) => error?.message || fallback;

  // Check for OAuth callback success/error
  useEffect(() => {
    const success = searchParams.get('oauth_success') || searchParams.get('success');
    const error = searchParams.get('oauth_error') || searchParams.get('error');
    const connected = searchParams.get('connected');
    const activationWarningParam = searchParams.get('activation_warning');

    if (success === 'true' && connected === 'true') {
      setSaveStatus({ upwork: 'success' });
      setActivationWarning(activationWarningParam ? decodeURIComponent(activationWarningParam) : null);
      setFeedback({
        tone: 'success',
        title: 'Upwork connected',
        message: activationWarningParam
          ? 'Propel connected successfully. Automatic setup needs one quick follow-up.'
          : 'Propel connected successfully and automatic setup is ready to continue.',
      });
      setTimeout(() => {
        setSaveStatus({});
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('oauth_success');
        newParams.delete('success');
        newParams.delete('connected');
        newParams.delete('activation_warning');
        setSearchParams(newParams);
      }, 3000);
      refreshConfig();
      loadOAuthStatus();
    } else if (error) {
      setSaveStatus({ upwork: 'error' });
      setActivationWarning(null);
      setFeedback({
        tone: 'error',
        title: 'OAuth connection failed',
        message: decodeURIComponent(error),
      });
      setTimeout(() => {
        setSaveStatus({});
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('oauth_error');
        newParams.delete('error');
        newParams.delete('activation_warning');
        setSearchParams(newParams);
      }, 5000);
    }
  }, [searchParams, setSearchParams, refreshConfig]);

  // Load OAuth status on mount
  useEffect(() => {
    if (tenant?._id) {
      loadOAuthStatus();
    }
  }, [tenant?._id]);

  // Load OAuth client credentials from config
  useEffect(() => {
    if (tenantConfig) {
      setOauthCredentials({
        clientId: tenantConfig.upwork?.clientId || '',
        clientSecret: '',
      });
      setHasClientSecretChanged(false);
    }
  }, [tenantConfig]);

  const loadOAuthStatus = async () => {
    if (!tenant?._id) return;
    
    setOauthLoading(true);
    try {
      const response = await getUpworkOAuthStatus();
      setOauthStatus(response.data);
    } catch (error) {
      console.error('Error loading OAuth status:', error);
    } finally {
      setOauthLoading(false);
    }
  };

  const loadProfileImportStatus = async () => {
    try {
      const response = await getProfileImportStatus();
      setProfileImportStatus(response.data || null);
    } catch (error) {
      console.error('Error loading profile import status:', error);
      setProfileImportStatus(null);
    }
  };

  useEffect(() => {
    if (tenant?._id) {
      loadProfileImportStatus();
    }
  }, [tenant?._id]);

  const handleSaveOAuthCredentials = async () => {
    if (!tenant?._id) {
      setFeedback({
        tone: 'error',
        title: 'Tenant not found',
        message: 'Reload this page and try again.',
      });
      return;
    }

    if (!oauthCredentials.clientId) {
      setFeedback({
        tone: 'error',
        title: 'Client ID required',
        message: 'Add your Upwork Client ID before saving credentials.',
      });
      return;
    }

    setLoading(true);
    setSaveStatus({ oauthCredentials: 'saving' });
    setFeedback(null);

    try {
      const credentialsToSave = {
        clientId: oauthCredentials.clientId,
      };
      
      if (hasClientSecretChanged && oauthCredentials.clientSecret) {
        credentialsToSave.clientSecret = oauthCredentials.clientSecret;
      }
      
      await updateUpworkOAuthCredentials(tenant._id, credentialsToSave);
      setSaveStatus({ oauthCredentials: 'success' });
      setFeedback({
        tone: 'success',
        title: 'Credentials saved',
        message: 'Your Upwork OAuth credentials were updated successfully.',
      });
      setTimeout(() => setSaveStatus({}), 3000);
      setHasClientSecretChanged(false);
      await refreshConfig();
      await loadOAuthStatus();
    } catch (error) {
      console.error('Error saving OAuth credentials:', error);
      setSaveStatus({ oauthCredentials: 'error' });
      setFeedback({
        tone: 'error',
        title: 'Failed to save credentials',
        message: getErrorMessage(error, 'Please review your Client ID and Client Secret, then try again.'),
      });
      setTimeout(() => setSaveStatus({}), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectUpwork = async () => {
    if (!tenant?._id) {
      setFeedback({
        tone: 'error',
        title: 'Tenant not found',
        message: 'Reload this page and try again.',
      });
      return;
    }

    if (!oauthCredentials.clientId) {
      setFeedback({
        tone: 'error',
        title: 'Client ID required',
        message: 'Configure your Upwork Client ID before starting OAuth.',
      });
      return;
    }

    setOauthLoading(true);
    setFeedback(null);
    try {
      const response = await initiateUpworkOAuth(tenant._id);
      window.location.href = response.authorizationUrl;
    } catch (error) {
      console.error('Error initiating OAuth flow:', error);
      setFeedback({
        tone: 'error',
        title: 'Failed to start OAuth',
        message: getErrorMessage(error, 'Propel could not start the Upwork authorization flow.'),
      });
      setOauthLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    if (!tenant?._id) return;

    setOauthLoading(true);
    setFeedback(null);
    try {
      await refreshUpworkToken();
      await loadOAuthStatus();
      await refreshConfig();
      setSaveStatus({ refresh: 'success' });
      setFeedback({
        tone: 'success',
        title: 'Token refreshed',
        message: 'Your Upwork connection was refreshed successfully.',
      });
      setTimeout(() => setSaveStatus({}), 3000);
    } catch (error) {
      console.error('Error refreshing token:', error);
      setFeedback({
        tone: 'error',
        title: 'Refresh failed',
        message: getErrorMessage(error, 'Propel could not refresh the Upwork token.'),
      });
    } finally {
      setOauthLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!tenant?._id) return;

    // Step-up auth: require password confirmation before disconnect
    const stepUpToken = await requireStepUp();
    if (!stepUpToken) return; // user cancelled

    setOauthLoading(true);
    setFeedback(null);
    try {
      await disconnectUpwork(stepUpToken);
      await loadOAuthStatus();
      await refreshConfig();
      setSaveStatus({ disconnect: 'success' });
      setFeedback({
        tone: 'success',
        title: 'Upwork disconnected',
        message: 'The OAuth connection was removed successfully.',
      });
      setTimeout(() => setSaveStatus({}), 3000);
    } catch (error) {
      console.error('Error disconnecting:', error);
      setFeedback({
        tone: 'error',
        title: 'Disconnect failed',
        message: getErrorMessage(error, 'Propel could not disconnect this Upwork account.'),
      });
    } finally {
      setOauthLoading(false);
    }
  };

  const getStatusIcon = (type) => {
    const status = saveStatus[type];
    if (status === 'saving') return <Loader className="w-4 h-4 animate-spin" />;
    if (status === 'success') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'error') return <XCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  const formatExpirationDate = (dateString) => {
    if (!dateString) return { text: 'N/A', urgency: 'none' };
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) return { text: 'Expired', urgency: 'critical' };
    if (diffHours < 1) return { text: 'Expiring soon', urgency: 'critical' };
    if (diffHours < 24) return { text: `Expires in ${diffHours} hours`, urgency: 'warning' };
    if (diffDays < 3) return { text: `Expires in ${diffDays} days`, urgency: 'warning' };
    return { text: `Expires in ${diffDays} days`, urgency: 'normal' };
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'text-red-600 font-semibold';
      case 'warning': return 'text-accent-600 font-medium';
      default: return 'text-gray-900 font-medium';
    }
  };

  const getConnectionStatusBadge = () => {
    if (oauthLoading) {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm flex items-center gap-2">
          <Loader className="w-3 h-3 animate-spin" />
          Loading...
        </span>
      );
    }

    if (!oauthStatus) {
      return (
        <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
          Unknown
        </span>
      );
    }

    const status = oauthStatus.status;
    const statusMap = {
      connected: { bg: 'bg-green-100', text: 'text-green-700', label: 'Connected' },
      expired_refreshable: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Expired (Refreshable)' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Expired' },
      not_authorized: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Not Authorized' },
      no_credentials: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'No Credentials' },
      disconnected: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Disconnected' },
    };

    const statusStyle = statusMap[status] || statusMap.disconnected;
    return (
      <span className={`px-3 py-1 ${statusStyle.bg} ${statusStyle.text} rounded-full text-sm`}>
        {statusStyle.label}
      </span>
    );
  };

  const TABS = [
    { key: 'upwork', label: 'Upwork', icon: Briefcase },
    { key: 'notifications', label: 'Notification Channels', icon: Bell },
  ];

  const [activeTab, setActiveTab] = useState('upwork');

  return (
    <AdminPageLayout>
      <StepUpAuthModal {...stepUpModalProps} />
      <AdminCard bodyClassName="space-y-6">
        {/* Inner Tab Bar */}
        <div className="border-b border-gray-200 -mx-6 px-6 pb-3">
          <nav className="flex -mb-px overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Upwork Tab */}
        {activeTab === 'upwork' && (
        <div className="space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Briefcase className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold">Upwork OAuth 2.0</h3>
          </div>
          {getConnectionStatusBadge()}
        </div>

        {activationWarning && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {activationWarning}
          </div>
        )}

        {feedback && (
          <div
            className={`rounded-lg border p-4 text-sm ${
              feedback.tone === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-rose-200 bg-rose-50 text-rose-800'
            }`}
          >
            <div className="flex items-start gap-3">
              {feedback.tone === 'success' ? (
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              )}
              <div>
                <p className="font-medium">{feedback.title}</p>
                <p className="mt-1">{feedback.message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-primary-200 bg-primary-50 p-4">
          <h4 className="text-sm font-semibold text-primary-900">Choose your Upwork access mode</h4>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-green-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">OAuth Full Sync</p>
                  <p className="mt-1 text-xs text-gray-600">
                    Sync jobs, proposals, and messages automatically using your tenant-owned Client ID and Client Secret.
                  </p>
                </div>
                <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">Recommended</span>
              </div>
            </div>
            <div
              className="rounded-lg border border-blue-200 bg-white p-4"
              data-onboarding="browser-assist-import-card"
            >
              <p className="font-medium text-gray-900">Browser-Assist Fallback</p>
              <p className="mt-1 text-xs text-gray-600">
                Import reviewed Upwork profile pages through the Chrome extension. Propel will still analyze jobs, draft proposals, and help you apply on Upwork, but it will not API-sync jobs or messages.
              </p>
              {profileImportStatus && (
                <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  <p className="font-medium">
                    {profileImportStatus.ready ? 'Browser-Assist mode is ready' : 'Browser-Assist mode not finished yet'}
                  </p>
                  <p className="mt-1">
                    Coverage:
                    {' '}
                    {profileImportStatus.coverage?.freelancerProfile ? 'profile' : 'profile missing'}
                    {', '}
                    {profileImportStatus.coverage?.portfolio ? 'portfolio' : 'portfolio missing'}
                    {', '}
                    {profileImportStatus.coverage?.employmentEducation ? 'experience added' : 'experience optional'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* OAuth Client Credentials Setup */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">Step 1: Configure OAuth Credentials</h4>
          <p className="text-sm text-gray-600 mb-4">
            Get your Client ID and Client Secret from{' '}
            <a 
              href="https://www.upwork.com/developer/keys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Upwork Developer Keys
            </a>
            {' '}(select Key Type: OAuth 2.0)
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Client ID *
              </label>
              <input
                type="text"
                value={oauthCredentials.clientId}
                onChange={(e) => setOauthCredentials({ ...oauthCredentials, clientId: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Enter Upwork Client ID"
                disabled={!canEdit}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Client Secret *
              </label>
              <input
                type="password"
                value={oauthCredentials.clientSecret}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setOauthCredentials({ ...oauthCredentials, clientSecret: newValue });
                  setHasClientSecretChanged(newValue !== '');
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder={tenantConfig?.upwork?.hasClientSecret && !hasClientSecretChanged ? '••••••••' : 'Enter Upwork Client Secret'}
                disabled={!canEdit}
                required
              />
              {tenantConfig?.upwork?.hasClientSecret && !hasClientSecretChanged && (
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing secret</p>
              )}
            </div>
          </div>

          <button
            onClick={handleSaveOAuthCredentials}
            disabled={!canEdit || loading || !oauthCredentials.clientId}
            className="mt-4 px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
          >
            {getStatusIcon('oauthCredentials')}
            <Save className="w-4 h-4" />
            Save OAuth Credentials
          </button>
        </div>

        {/* OAuth Connection Status */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">Step 2: Connect Upwork Account</h4>
          
          {oauthStatus && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium">{oauthStatus.message}</span>
                </div>
                {oauthStatus.tokenExpiresAt && (() => {
                  const expiry = formatExpirationDate(oauthStatus.tokenExpiresAt);
                  return (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-xs">Access Token:</span>
                      <span className={`text-xs ${getUrgencyColor(expiry.urgency)}`}>
                        {expiry.urgency === 'critical' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                        {expiry.urgency === 'warning' && <Clock className="w-3 h-3 inline mr-1" />}
                        {expiry.text}
                      </span>
                    </div>
                  );
                })()}
                {oauthStatus.refreshTokenExpiresAt && (() => {
                  const expiry = formatExpirationDate(oauthStatus.refreshTokenExpiresAt);
                  return (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-xs">Refresh Token:</span>
                      <span className={`text-xs ${getUrgencyColor(expiry.urgency)}`}>
                        {expiry.urgency === 'critical' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                        {expiry.urgency === 'warning' && <Clock className="w-3 h-3 inline mr-1" />}
                        {expiry.text}
                      </span>
                    </div>
                  );
                })()}
                {oauthStatus.organizationId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Organization ID:</span>
                    <span className="font-medium font-mono text-xs">{oauthStatus.organizationId}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {oauthStatus?.status === 'connected' ? (
              <>
                <button
                  onClick={handleRefreshToken}
                  disabled={oauthLoading || !canEdit}
                  className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  {oauthLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh Token
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={oauthLoading || !canEdit}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <Unlink className="w-4 h-4" />
                  Disconnect
                </button>
              </>
            ) : oauthStatus?.status === 'expired_refreshable' ? (
              <>
                <button
                  onClick={handleRefreshToken}
                  disabled={oauthLoading || !canEdit}
                  className="px-3 py-1.5 bg-accent-500 text-white rounded-md hover:bg-accent-600 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  {oauthLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh Token
                </button>
                <button
                  onClick={handleConnectUpwork}
                  disabled={oauthLoading || !oauthCredentials.clientId || !canEdit}
                  data-onboarding="connect-upwork-button"
                  className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <Link2 className="w-4 h-4" />
                  Reconnect
                </button>
              </>
            ) : (
              <button
                onClick={handleConnectUpwork}
                disabled={oauthLoading || !oauthCredentials.clientId || !canEdit}
                data-onboarding="connect-upwork-button"
                className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium transition-colors"
              >
                {oauthLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                Connect with Upwork
              </button>
            )}
          </div>

          {oauthStatus?.status === 'no_credentials' && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">OAuth credentials not configured</p>
                <p className="mt-1">Please configure your Client ID and Client Secret in Step 1 above.</p>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Fallback: Import Upwork Profile Pages</h4>
          <p className="text-sm text-gray-700">
            Use this when Upwork does not allow OAuth credentials for your account. Open your Upwork freelancer profile page, launch the Propel Chrome extension side panel, review the parsed data, and confirm the import.
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-blue-200 bg-white p-3 text-sm text-gray-700">
              <p className="font-medium text-gray-900">What you still get</p>
              <ul className="mt-2 list-disc pl-4 space-y-1 text-xs text-gray-600">
                <li>Profile-driven job fit analysis on Upwork pages</li>
                <li>Proposal drafting and apply assistance in the extension</li>
                <li>Imported skills, overview, and portfolio context for RAG</li>
              </ul>
            </div>
            <div className="rounded-md border border-blue-200 bg-white p-3 text-sm text-gray-700">
              <p className="font-medium text-gray-900">What stays unavailable without OAuth</p>
              <ul className="mt-2 list-disc pl-4 space-y-1 text-xs text-gray-600">
                <li>Automatic job sync into the Propel feed</li>
                <li>Automatic proposal and message sync from Upwork</li>
                <li>Live profile refresh from the Upwork API</li>
              </ul>
            </div>
          </div>
          {profileImportStatus?.missingRecommendedPages?.length > 0 && (
            <p className="mt-3 text-xs text-blue-700">
              Still recommended to import: {profileImportStatus.missingRecommendedPages.join(', ').replaceAll('_', ' ')}
            </p>
          )}
        </div>
        <UpworkMcpConnectionCard />
      </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <NotificationChannels />
      )}
      </AdminCard>
    </AdminPageLayout>
  );
}
